import * as React from 'react'

import { useTypingAssist } from '@/lib/use-typing-assist'
import { cn } from '@/lib/utils'

/**
 * Textarea sem moldura que cresce com o texto — o título e os detalhes do
 * lançamento são "folha de papel", não caixa de formulário. O foco aparece
 * pelo fundo e pelo anel (quem chama passa as classes).
 */
export const AutoTextarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<'textarea'> & { typingAssist?: boolean }>(
  ({ className, value, onInput, typingAssist = true, ...props }, forwarded) => {
    const inner = React.useRef<HTMLTextAreaElement | null>(null)
    const assist = useTypingAssist(typingAssist, forwarded)
    const assistRef = assist.ref
    const ref = React.useCallback(
      (el: HTMLTextAreaElement | null) => {
        inner.current = el
        return assistRef(el)
      },
      [assistRef],
    )

    const fit = React.useCallback(() => {
      const el = inner.current
      if (!el) return
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }, [])

    React.useLayoutEffect(fit, [value, fit])

    // A largura mudou (diálogo abriu, janela girou): o texto quebra em outro ponto.
    React.useEffect(() => {
      const el = inner.current
      if (!el || typeof ResizeObserver === 'undefined') return
      let width = el.clientWidth
      const ro = new ResizeObserver(() => {
        if (el.clientWidth === width) return
        width = el.clientWidth
        fit()
      })
      ro.observe(el)
      return () => ro.disconnect()
    }, [fit])

    return (
      <textarea
        rows={1}
        spellCheck={assist.spellCheck}
        autoCapitalize={assist.autoCapitalize}
        {...props}
        ref={ref}
        value={value}
        onInput={(e) => {
          fit()
          onInput?.(e)
        }}
        className={cn(
          'block w-full resize-none overflow-hidden bg-transparent transition-colors focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
      />
    )
  },
)
AutoTextarea.displayName = 'AutoTextarea'

/**
 * Texto de uma ação em formulário com moldura de campo: quebra linha e cresce,
 * em vez de cortar. Enter envia o formulário; Shift+Enter quebra a linha.
 */
export const ActionTextarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<typeof AutoTextarea>>(
  ({ className, onKeyDown, ...props }, ref) => (
    <AutoTextarea
      ref={ref}
      maxLength={1000}
      {...props}
      onKeyDown={(e) => {
        onKeyDown?.(e)
        if (e.defaultPrevented) return
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault()
          e.currentTarget.form?.requestSubmit()
        }
      }}
      className={cn(
        'min-h-9 whitespace-pre-wrap break-words rounded-md border border-input bg-background px-3 py-2 text-sm leading-5 shadow-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        className,
      )}
    />
  ),
)
ActionTextarea.displayName = 'ActionTextarea'
